import { memo } from "react";

import type { BusbarSelectionDraft, DeratingFactorDraft } from "./busbarSelectionDraft";
import {
  anyFilled,
  blockMemo,
  fieldKit,
  OptionalBlock,
  type BlockProps,
} from "./BusbarSelectionFields";

type DeratingFactorRowProps = {
  row: DeratingFactorDraft;
  // Zero-based position in the list; shown to the user as "Derating factor 1".
  index: number;
  onChange: (rowId: string, patch: Partial<DeratingFactorDraft>) => void;
  onRemove: (rowId: string) => void;
};

// One derating factor of the ampacity evidence. The row holds no state of its own: every edit
// goes up as a patch with the row's id, so the parent callbacks stay the same between renders
// and a row renders again only when its own values change.
export const DeratingFactorRow = memo(function DeratingFactorRow({
  row,
  index,
  onChange,
  onRemove,
}: DeratingFactorRowProps) {
  const number = index + 1;
  const kit = fieldKit(
    row,
    (patch) => onChange(row.id, patch),
    (field) => `derating-${number}-${field}`,
  );

  return (
    <fieldset data-derating-row={row.id}>
      <legend>Derating factor {number}</legend>
      {kit.text("Name", "name")}
      {kit.decimal("Factor", "value")}
      {kit.text("Source", "source")}
      {kit.reference(["document", "revisionOrDate", "pageOrClause"])}
      <button type="button" onClick={() => onRemove(row.id)}>
        Remove derating factor {number}
      </button>
    </fieldset>
  );
});

const AMPACITY_KEYS = [
  "baseAmpacityA",
  "baseAmpacitySource",
  "deratingFactors",
  "baseAmpacityAtSiteConditions",
  "siteConditionsSource",
  "ampacityDocument",
  "ampacityRevisionOrDate",
  "ampacityPageOrClause",
] as const;

type AmpacityValues = Pick<BusbarSelectionDraft, (typeof AMPACITY_KEYS)[number]>;

type AmpacityBlockProps = BlockProps<AmpacityValues> & {
  onAddRow: () => void;
  onRowChange: (rowId: string, patch: Partial<DeratingFactorDraft>) => void;
  onRowRemove: (rowId: string) => void;
};

export const AmpacityBlock = blockMemo(function AmpacityBlock({
  draft,
  onChange,
  disabled,
  onAddRow,
  onRowChange,
  onRowRemove,
}: AmpacityBlockProps) {
  const kit = fieldKit(draft, onChange);
  return (
    <OptionalBlock
      title="Ampacity evidence"
      filled={
        anyFilled(draft.baseAmpacityA, draft.baseAmpacitySource, draft.baseAmpacityAtSiteConditions, draft.siteConditionsSource) ||
        draft.deratingFactors.length > 0
      }
      disabled={disabled}
    >
      {kit.decimal("Base ampacity (A)", "baseAmpacityA")}
      {kit.text("Base ampacity source", "baseAmpacitySource")}
      {kit.yesNo("Base ampacity is at site conditions", "baseAmpacityAtSiteConditions")}
      {kit.text("Site conditions source", "siteConditionsSource")}
      {kit.reference(["ampacityDocument", "ampacityRevisionOrDate", "ampacityPageOrClause"])}
      <p>
        <strong>Derating factors</strong>
      </p>
      {draft.deratingFactors.map((row, index) => (
        <DeratingFactorRow
          key={row.id}
          row={row}
          index={index}
          onChange={onRowChange}
          onRemove={onRowRemove}
        />
      ))}
      <button type="button" onClick={onAddRow}>
        Add derating factor
      </button>
    </OptionalBlock>
  );
}, AMPACITY_KEYS);
